import { jsx as _jsx } from "react/jsx-runtime";
import { getInjectionsForObjectType } from './tabInjectionRegistry';
import ComponentRenderer from '@helper/ComponentRenderer/ComponentRenderer';
import { NotAuthorized } from '@core/layout/components/ErrorPages';
const csv = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);
function isAllowed(appRoles, userGroups, userAppRoles, userGroupKey) {
    if (appRoles.length === 0 && userGroups.length === 0)
        return true;
    if (userGroups.length > 0 && userGroupKey && userGroups.includes(userGroupKey))
        return true;
    if (appRoles.length > 0) {
        const all = Object.values(userAppRoles || {}).flat();
        if (appRoles.some(r => all.includes(r)))
            return true;
    }
    return false;
}
// Entry from an admin DB ObjectTab (CSV roles). Denied tabs hide unless showUnauthorizedError.
function dbEntry(tab, mergedProps, userAppRoles, userGroupKey) {
    const allowed = isAllowed(csv(tab.appRoles), csv(tab.userGroups), userAppRoles, userGroupKey);
    if (!allowed && !tab.showUnauthorizedError)
        return null;
    return {
        id: tab.tabId || tab.contributionId,
        icon: tab.icon || 'fas puzzle-piece',
        label: tab.name,
        content: allowed ? buildComponentContent(tab.components, mergedProps) : _jsx(NotAuthorized, { mode: "inline" }),
        refreshable: true,
    };
}
function buildComponentContent(components, passedProps) {
    const sorted = [...components].sort((a, b) => a.priority - b.priority);
    return (_jsx("div", { className: "uxpcore_injectedtab__content", children: sorted.map(comp => {
            var _a;
            // Component IDs are 3-part: moduleId/type/componentId or 2-part: moduleId/componentId
            const parts = comp.componentId.split('/');
            const moduleId = parts[0];
            const componentId = parts.length >= 3 ? parts[2] : parts[1];
            const configuredProps = (_a = comp.configuredProps) !== null && _a !== void 0 ? _a : {};
            const hasConfigured = Object.keys(configuredProps).length > 0;
            return (_jsx(ComponentRenderer, { moduleId: moduleId, componentId: componentId, configuredProps: configuredProps, hasConfigured: hasConfigured, additionalProps: passedProps, configNeededMessage: "Configure this widget's settings in the Page Editor" }, comp.componentId));
        }) }));
}
export function buildInjectedAdditionalDetails(options) {
    const { objectTabs, objectType, objectKey, parentProps = {}, userAppRoles = {}, userGroupKey = '' } = options;
    const mergedProps = Object.assign(Object.assign({}, parentProps), { objectKey, objectType });
    const key = objectType.toLowerCase();
    // Admin DB overrides (disable / role edits) — win over registered injections by contributionId.
    // A DB row customises a code registration; with no registration for it, it is skipped.
    const registered = getInjectionsForObjectType(key);
    const registeredIds = new Set(registered.map(i => i.id.toLowerCase()));
    const dbTabs = ((objectTabs === null || objectTabs === void 0 ? void 0 : objectTabs[key]) || []).filter(t => { var _a; return registeredIds.has(((_a = t.contributionId) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || ''); });
    const dbInjectionIds = new Set(dbTabs.map(t => { var _a; return (_a = t.contributionId) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }).filter(Boolean));
    const configuredEntries = [...dbTabs]
        .filter(tab => tab.status !== 'Disabled')
        .sort((a, b) => a.priority - b.priority)
        .map(tab => dbEntry(tab, mergedProps, userAppRoles, userGroupKey))
        .filter(Boolean);
    // Registered injections — complete data: the app's uxp.ts wrapper merged the bundle.json
    // objectTabs entry (metadata/roles/showUnauthorizedError) at registration, exactly like
    // registerComponent. The panel's spinner (driven by the backend manifest) covers the gap
    // until registration happens.
    const injectedEntries = registered
        .filter(inj => !dbInjectionIds.has(inj.id.toLowerCase()))
        .map(inj => {
        const allowed = isAllowed(inj.appRoles || [], inj.userGroups || [], userAppRoles, userGroupKey);
        if (!allowed && !inj.showUnauthorizedError)
            return null;
        return {
            id: inj.id,
            icon: inj.icon || 'fas puzzle-piece',
            label: inj.name || inj.id,
            content: allowed ? buildComponentContent(inj.components || [], mergedProps) : _jsx(NotAuthorized, { mode: "inline" }),
            refreshable: inj.refreshable !== false,
            events: inj.events,
        };
    })
        .filter(Boolean);
    return [...configuredEntries, ...injectedEntries];
}
